'use client';

import { c as _c } from "react/compiler-runtime";
import { type Factiii } from '@shared/all/domains/factiiis';
import { factiiiDisplayName } from '@shared/all/helpers';
import { buildChatLink } from '@shared/all/helpers/chatLink';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import useUser from '@shared/utils/hooks/useUser';
import { useQuery } from '@tanstack/react-query';
import { Bot, MessageCircle, Shield, Users } from 'lucide-react';
import Link from 'next/link';
import { useState } from 'react';
import { Tooltip } from 'react-tooltip';
import { allRequirementOptions } from '../../utils/requirementOptions';
import { client } from '../../utils/trpc';
import Avatar from '../common/Avatar';
import Button from '../common/Button';
import PageBanner, { BannerAction, BannerChip } from '../common/PageBanner';
import FactiiiEdit from '../factiiis/FactiiiEdit';
import FactiiiGuide from '../factiiis/FactiiiGuide';
import { GroupFactiiisModal } from '../factiiis/GroupFactiiisModal';
import ModerationPanel from '../factiiis/ModerationPanel';
import { FactiiiSelectorModal } from './FactiiiSelectorModal';
type Props = {
  factiii: Factiii;
  allFactiiis?: Factiii[];
  onFactiiiSelect?: (factiiiId: number) => void;
};
const FactiiiHeader = t0 => {
  const $ = _c(106);
  const {
    factiii,
    allFactiiis: t1,
    onFactiiiSelect
  } = t0;
  const allFactiiis = t1 === undefined ? [] : t1;
  const {
    currentUser
  } = useUser();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [moderationPanelOpen, setModerationPanelOpen] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showFactiiiSelectorModal, setShowFactiiiSelectorModal] = useState(false);
  const t2 = factiii?.spaceSlug;
  let t3;
  if ($[0] !== currentUser.username || $[1] !== t2) {
    t3 = ["space-membership", t2, currentUser.username];
    $[0] = currentUser.username;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== factiii.spaceSlug) {
    t4 = () => unwrapFromResult(client.spaces.getMembership.query({
      slug: factiii?.spaceSlug || ""
    }));
    $[3] = factiii.spaceSlug;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = Boolean(factiii?.spaceSlug) && currentUser.isAuthenticated === true;
  let t6;
  if ($[5] !== t3 || $[6] !== t4 || $[7] !== t5) {
    t6 = {
      queryKey: t3,
      queryFn: t4,
      staleTime: 300000,
      enabled: t5
    };
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const {
    data: spaceMembership
  } = useQuery(t6);
  let t7;
  if ($[9] !== currentUser.isAuthenticated || $[10] !== currentUser.username || $[11] !== factiii.owner?.username || $[12] !== spaceMembership) {
    t7 = currentUser.isAuthenticated && (currentUser.username === factiii.owner?.username || spaceMembership?.roles && (spaceMembership.roles.includes("MODERATOR") || spaceMembership.roles.includes("OWNER")));
    $[9] = currentUser.isAuthenticated;
    $[10] = currentUser.username;
    $[11] = factiii.owner?.username;
    $[12] = spaceMembership;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const canModerate = t7;
  let t8;
  if ($[14] !== factiii.requirements) {
    t8 = factiii.requirements.includes("POSTFACTIII_MODERATION_REQUIRED");
    $[14] = factiii.requirements;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const requiresModeration = t8;
  let t9;
  if ($[16] !== factiii.autoTagging?.taggableFactiiis) {
    t9 = factiii.autoTagging?.taggableFactiiis ?? [];
    $[16] = factiii.autoTagging?.taggableFactiiis;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const taggableFactiiis = t9;
  const t10 = "flex";
  const t11 = "w-full";
  const T0 = PageBanner;
  const t12 = "factiii";
  const t13 = "factiii-header";
  const t14 = "factiii-banner";
  const t15 = String(factiii.id);
  const t16 = typeof factiii.bannerKey === "string" ? factiii.bannerKey : null;
  const t17 = "Header Image for " + factiii.name;
  const t18 = `${factiii.id}-${factiii.avatarKey ?? "default"}`;
  const t19 = `border-4 border-background-secondary ${factiii.status === "RETIRED" ? "grayscale" : ""}`;
  let t20;
  if ($[18] !== factiii.avatarKey || $[19] !== factiii.id || $[20] !== t18 || $[21] !== t19) {
    t20 = <Avatar key={t18} factiiiAvatarKey={factiii.avatarKey} factiiiId={factiii.id} size={100} className={t19} />;
    $[18] = factiii.avatarKey;
    $[19] = factiii.id;
    $[20] = t18;
    $[21] = t19;
    $[22] = t20;
  } else {
    t20 = $[22];
  }
  const t21 = allFactiiis.length > 1 && onFactiiiSelect && <div className="absolute left-full top-0 z-10 ml-2 flex w-max flex-row gap-1">{allFactiiis.filter(f => f.id !== factiii.id).slice(0, 3).map(otherFactiii => <button key={otherFactiii.id} onClick={() => onFactiiiSelect(otherFactiii.id)} className="group relative z-10" title={`Switch to ${otherFactiii.name} (${otherFactiii.requirements.join(", ")})`}><div className="relative"><Avatar factiiiAvatarKey={otherFactiii.avatarKey} factiiiId={otherFactiii.id} size={32} className="border-2 border-background-primary shadow-sm transition-all duration-200 hover:scale-110 hover:shadow-md" /><div className="absolute -bottom-1 -left-1 size-2 rounded-full bg-background-secondary" /></div></button>)}{allFactiiis.length > 4 && <button onClick={() => setShowFactiiiSelectorModal(true)} className="flex size-8 items-center justify-center rounded-full border-2 border-background-primary bg-background-secondary text-xs font-emphasis text-textSecondary shadow-sm transition-all duration-200 hover:bg-background-light" title={`View all ${allFactiiis.length} factiiis`}>+{allFactiiis.length - 4}</button>}</div>;
  let t22;
  if ($[23] !== canModerate || $[24] !== requiresModeration || $[25] !== setModerationPanelOpen) {
    t22 = canModerate && requiresModeration && <Button text="Moderate" icon={<Shield className="size-4" />} classNames="px-2.5 py-1 text-xs" variant="CLEAR" onClick={() => setModerationPanelOpen(true)} />;
    $[23] = canModerate;
    $[24] = requiresModeration;
    $[25] = setModerationPanelOpen;
    $[26] = t22;
  } else {
    t22 = $[26];
  }
  let t23;
  if ($[27] !== currentUser.isAuthenticated || $[28] !== currentUser.username || $[29] !== factiii.owner || $[30] !== setEditModalOpen) {
    t23 = factiii.owner && currentUser.username == factiii.owner.username && currentUser.isAuthenticated && <Button text="Edit Factiii" classNames="px-2.5 py-1 text-xs" variant="CLEAR" onClick={() => setEditModalOpen(true)} />;
    $[27] = currentUser.isAuthenticated;
    $[28] = currentUser.username;
    $[29] = factiii.owner;
    $[30] = setEditModalOpen;
    $[31] = t23;
  } else {
    t23 = $[31];
  }
  let t24;
  if ($[32] !== t22 || $[33] !== t23) {
    t24 = <>{t22}{t23}</>;
    $[32] = t22;
    $[33] = t23;
    $[34] = t24;
  } else {
    t24 = $[34];
  }
  let t25;
  if ($[35] !== factiii.name) {
    t25 = factiiiDisplayName(factiii.name);
    $[35] = factiii.name;
    $[36] = t25;
  } else {
    t25 = $[36];
  }
  let t26;
  if ($[37] !== factiii.status) {
    t26 = factiii.status === "RETIRED" && <span className="text-textGray">(Retired)</span>;
    $[37] = factiii.status;
    $[38] = t26;
  } else {
    t26 = $[38];
  }
  let t27;
  if ($[39] !== t25 || $[40] !== t26) {
    t27 = <span data-id="factiii-name">{t25}{" "}{t26}</span>;
    $[39] = t25;
    $[40] = t26;
    $[41] = t27;
  } else {
    t27 = $[41];
  }
  let t28;
  if ($[42] !== factiii.owner || $[43] !== factiii.space) {
    t28 = factiii.space ? <BannerChip dataId="factiii-owner" href={`/s/${factiii.space.slug}`} icon={<Avatar spaceAvatarKey={factiii.space.avatarKey} size={16} />} text={factiii.space.name} title={`Owned by the ${factiii.space.name} space`} /> : factiii.owner && <BannerChip dataId="factiii-owner" href={`/user/${factiii.owner.username}`} text={`@${factiii.owner.username}`} title={`Owned by @${factiii.owner.username}`} />;
    $[42] = factiii.owner;
    $[43] = factiii.space;
    $[44] = t28;
  } else {
    t28 = $[44];
  }
  let t29;
  if ($[45] !== factiii.id || $[46] !== factiii.name || $[47] !== factiii.space) {
    t29 = factiii.space && <BannerAction dataId="factiii-chat" href={buildChatLink({
      spaceSlug: factiii.space.slug,
      factiiiId: factiii.id
    })} icon={<MessageCircle size={15} />} text="Chat" title={`Chat about ${factiii.name}`} />;
    $[45] = factiii.id;
    $[46] = factiii.name;
    $[47] = factiii.space;
    $[48] = t29;
  } else {
    t29 = $[48];
  }
  let t30;
  if ($[49] !== factiii.description) {
    t30 = factiii.description && <span data-id="factiii-description">{factiii.description}</span>;
    $[49] = factiii.description;
    $[50] = t30;
  } else {
    t30 = $[50];
  }
  let t31;
  if ($[51] !== factiii.data || $[52] !== factiii.name) {
    t31 = factiii?.name.toLowerCase() === "budget".toLowerCase() && factiii.data && <div className="px-4 text-sm text-textSecondary/70">Budget: {factiii.data}</div>;
    $[51] = factiii.data;
    $[52] = factiii.name;
    $[53] = t31;
  } else {
    t31 = $[53];
  }
  let t32;
  if ($[54] !== factiii.requirements) {
    t32 = factiii.requirements.length > 0 && <div className="space-y-2 px-4"><h3 className="text-xs font-emphasis uppercase tracking-wide text-textSecondary">What's needed to post</h3><div className="flex flex-wrap gap-2">{factiii.requirements.map(_temp)}</div></div>;
    $[54] = factiii.requirements;
    $[55] = t32;
  } else {
    t32 = $[55];
  }
  let t33;
  if ($[56] !== factiii.autoTagging?.tagMultiplePerPost || $[57] !== taggableFactiiis) {
    t33 = taggableFactiiis.length > 0 && <div className="space-y-2 px-4"><h3 className="flex items-center gap-1.5 text-xs font-emphasis uppercase tracking-wide text-textSecondary"><Bot className="size-3.5" />Can tag these factiiis</h3><div className="flex flex-wrap gap-2">{taggableFactiiis.map(_temp2)}</div><p className="text-xs text-textSecondary">{factiii.autoTagging?.tagMultiplePerPost ? "Tags multiple per post." : "Tags one per post."}</p></div>;
    $[56] = factiii.autoTagging?.tagMultiplePerPost;
    $[57] = taggableFactiiis;
    $[58] = t33;
  } else {
    t33 = $[58];
  }
  let t34;
  if ($[59] !== factiii.groupId || $[60] !== setShowGroupModal) {
    t34 = factiii.groupId && <div className="space-y-2 px-4"><h3 className="text-xs font-emphasis uppercase tracking-wide text-textSecondary">Linked Factiiis</h3><Button variant="CLEAR" icon={<Users size={16} />} text="View related factiiis" tooltip="This factiii is part of a group where only one can be active at a time. Click to see other options." onClick={() => setShowGroupModal(true)} classNames="text-primary hover:text-primary text-xs" /></div>;
    $[59] = factiii.groupId;
    $[60] = setShowGroupModal;
    $[61] = t34;
  } else {
    t34 = $[61];
  }
  let t35;
  if ($[62] !== T0 || $[63] !== t15 || $[64] !== t16 || $[65] !== t17 || $[66] !== t20 || $[67] !== t21 || $[68] !== t24 || $[69] !== t27 || $[70] !== t28 || $[71] !== t29 || $[72] !== t30 || $[73] !== t31 || $[74] !== t32 || $[75] !== t33 || $[76] !== t34) {
    t35 = <T0 variant={t12} dataId={t13} bannerDataId={t14} entityKey={t15} bannerKey={t16} bannerAlt={t17} avatar={t20} avatarAside={t21} coverActions={t24} title={t27} subtitle={t28} actions={t29} description={t30}>{t31}{t32}{t33}{t34}</T0>;
    $[62] = T0;
    $[63] = t15;
    $[64] = t16;
    $[65] = t17;
    $[66] = t20;
    $[67] = t21;
    $[68] = t24;
    $[69] = t27;
    $[70] = t28;
    $[71] = t29;
    $[72] = t30;
    $[73] = t31;
    $[74] = t32;
    $[75] = t33;
    $[76] = t34;
    $[77] = t35;
  } else {
    t35 = $[77];
  }
  let t36;
  if ($[78] !== factiii.id) {
    t36 = <FactiiiGuide factiiiId={factiii.id} />;
    $[78] = factiii.id;
    $[79] = t36;
  } else {
    t36 = $[79];
  }
  let t37;
  if ($[80] !== editModalOpen || $[81] !== factiii || $[82] !== setEditModalOpen) {
    t37 = editModalOpen && <FactiiiEdit closeHandler={() => setEditModalOpen(false)} factiiiId={factiii.id} factiii={factiii} spaceSlug={factiii.spaceSlug} username={factiii.username ?? undefined} />;
    $[80] = editModalOpen;
    $[81] = factiii;
    $[82] = setEditModalOpen;
    $[83] = t37;
  } else {
    t37 = $[83];
  }
  let t38;
  if ($[84] !== factiii.id || $[85] !== moderationPanelOpen || $[86] !== setModerationPanelOpen) {
    t38 = moderationPanelOpen && <ModerationPanel closeHandler={() => setModerationPanelOpen(false)} factiiiId={factiii.id} />;
    $[84] = factiii.id;
    $[85] = moderationPanelOpen;
    $[86] = setModerationPanelOpen;
    $[87] = t38;
  } else {
    t38 = $[87];
  }
  let t39;
  if ($[88] !== factiii.groupId || $[89] !== factiii.name || $[90] !== setShowGroupModal || $[91] !== showGroupModal) {
    t39 = showGroupModal && factiii.groupId && <GroupFactiiisModal closeHandler={() => setShowGroupModal(false)} groupId={factiii.groupId} currentFactiiiName={factiii.name} />;
    $[88] = factiii.groupId;
    $[89] = factiii.name;
    $[90] = setShowGroupModal;
    $[91] = showGroupModal;
    $[92] = t39;
  } else {
    t39 = $[92];
  }
  let t40;
  if ($[93] !== allFactiiis || $[94] !== factiii || $[95] !== onFactiiiSelect || $[96] !== setShowFactiiiSelectorModal || $[97] !== showFactiiiSelectorModal) {
    t40 = showFactiiiSelectorModal && allFactiiis.length > 0 && onFactiiiSelect && <FactiiiSelectorModal closeHandler={() => setShowFactiiiSelectorModal(false)} allFactiiis={allFactiiis} currentFactiii={factiii} onFactiiiSelect={onFactiiiSelect} />;
    $[93] = allFactiiis;
    $[94] = factiii;
    $[95] = onFactiiiSelect;
    $[96] = setShowFactiiiSelectorModal;
    $[97] = showFactiiiSelectorModal;
    $[98] = t40;
  } else {
    t40 = $[98];
  }
  let t41;
  if ($[99] !== t35 || $[100] !== t36 || $[101] !== t37 || $[102] !== t38 || $[103] !== t39 || $[104] !== t40) {
    t41 = <div className={t10}><div className={t11}>{t35}{t36}{t37}{t38}{t39}{t40}</div></div>;
    $[99] = t35;
    $[100] = t36;
    $[101] = t37;
    $[102] = t38;
    $[103] = t39;
    $[104] = t40;
    $[105] = t41;
  } else {
    t41 = $[105];
  }
  return t41;
};
export default FactiiiHeader;
function _temp(requirement) {
  const reqInfo = allRequirementOptions.find(opt => opt.value === requirement);
  if (!reqInfo) {
    return null;
  }
  const IconComponent = reqInfo.icon;
  const tooltipId = `req-${requirement}`;
  return <div key={requirement}><div className="flex cursor-help items-center gap-1.5 rounded-full border border-ds-border-standard bg-background-primary px-3 py-1.5 text-xs font-emphasis text-textSecondary transition-colors hover:border-primary/50 hover:text-textPrimary" data-tooltip-id={tooltipId} data-tooltip-content={reqInfo.description}>{IconComponent && <IconComponent className="size-3.5" />}<span>{reqInfo.label}</span></div><Tooltip id={tooltipId} /></div>;
}
function _temp2(taggable) {
  return <Link key={taggable.id} href={`/fids/${taggable.id}`} className="flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-3 py-1.5 text-xs font-emphasis text-primary transition-colors hover:bg-primary/20"><Avatar factiiiId={taggable.id} factiiiAvatarKey={taggable.avatarKey} size={16} /><span>{taggable.name}</span></Link>;
}